import renderActionButton from '../utils/renderActionButton';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { ClientItemId } from 'owa-client-ids';
import loc from 'owa-localize';
import { itemHeaderForward } from 'owa-locstrings/lib/strings/itemheaderforward.locstring.json';
import { itemHeaderReply } from 'owa-locstrings/lib/strings/itemheaderreply.locstring.json';
import { itemHeaderReplyAll } from 'owa-locstrings/lib/strings/itemheaderreplyall.locstring.json';
import { lazyOpenCompose } from 'owa-mail-compose-actions';
import findInlineComposeViewState from 'owa-mail-compose-actions/lib/utils/findInlineComposeViewState';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { lazyForwardConversation, lazyReplyToConversation } from 'owa-mail-message-actions';
import checkItemReplyForwardDisabled from 'owa-mail-reading-pane-item-actions/lib/utils/checkItemReplyForwardDisabled';
import getLastestNonDraftItemId from 'owa-mail-reading-pane-store/lib/utils/getLastestNonDraftItemId';
import isNewestOnBottom from 'owa-mail-reading-pane-store/lib/utils/isNewestOnBottom';
import { InstrumentationContext } from 'owa-mail-store';
import mailStore from 'owa-mail-store/lib/store/Store';
import isReplyToAll from 'owa-readonly-recipient-well/lib/utils/isReplyToAll';
import Item from 'owa-service/lib/contract/Item';
import * as React from 'react';
import {
    listViewStore,
    shouldSuppressServerMarkReadOnReplyOrForward,
    TableQueryType,
} from 'owa-mail-list-store';

const styles = require('./GlobalReplyBar.scss');

interface GlobalReplyBarProps {
    conversationId: ClientItemId;
}

const Datapoint = {
    ReplyAction: 'GlobalReplyAction',
    ReplyAllAction: 'GlobalReplyAllAction',
    ForwardAction: 'GlobalForwardAction',
    OtherGlobalAction: 'GlobalActionOther',
};

export const GlobalReplyBar = observer(function GlobalReplyBar(props: GlobalReplyBarProps) {
    const latestNonDraftItemId = getLastestNonDraftItemId(props.conversationId);
    const message = latestNonDraftItemId && (mailStore.items.get(latestNonDraftItemId.Id) as Item);
    const [replyDisabled, replyAllDisabled, forwardDisabled] = checkItemReplyForwardDisabled(
        message,
        true /* isConversationItemPart */
    );
    const isSingleRecipient = !isReplyToAll(message);
    const importOpenCompose = () => {
        lazyOpenCompose.import();
    };
    const isComposing = findInlineComposeViewState(props.conversationId.Id);
    const onReply = (event?: React.MouseEvent<unknown>) => {
        handleActionClicked(Datapoint.ReplyAction, isSingleRecipient, event);
        replyToConversation(props.conversationId, false /* isReplyAll */);
    };
    const onReplyAll = (event?: React.MouseEvent<unknown>) => {
        handleActionClicked(Datapoint.ReplyAllAction, isSingleRecipient, event);
        replyToConversation(props.conversationId, true /* isReplyAll */);
    };
    const onForward = (event?: React.MouseEvent<unknown>) => {
        handleActionClicked(Datapoint.ForwardAction, isSingleRecipient, event);
        lazyForwardConversation.importAndExecute(
            props.conversationId,
            'ReadingPane',
            getInstrumentationContexts(),
            shouldSuppressServerMarkReadOnReplyOrForward(getTableView())
        );
    };

    const actionButtonClassName = styles.globalReplyAction;

    return !isComposing && latestNonDraftItemId ? (
        <div tabIndex={-1} className={styles.globalReplyBar} onMouseOver={importOpenCompose}>
            {renderButton(
                loc(itemHeaderReply),
                onReply,
                replyDisabled,
                actionButtonClassName,
                true /* addSeparator */
            )}

            {!isSingleRecipient &&
                renderButton(
                    loc(itemHeaderReplyAll),
                    onReplyAll,
                    replyAllDisabled,
                    actionButtonClassName,
                    true /* addSeparator */
                )}

            {renderButton(
                loc(itemHeaderForward),
                onForward,
                forwardDisabled,
                actionButtonClassName
            )}
        </div>
    ) : null;
});

function replyToConversation(conversationId, isReplyAll: boolean): void {
    lazyReplyToConversation.importAndExecute(
        conversationId,
        isReplyAll,
        'ReadingPane',
        getInstrumentationContexts(),
        shouldSuppressServerMarkReadOnReplyOrForward(getTableView())
    );
}

function renderButton(
    name: string,
    onclick: (event?: React.MouseEvent<unknown>) => void,
    disabled: boolean,
    className: string,
    addSeparator?: boolean
): JSX.Element {
    return (
        <>
            {renderActionButton(name, onclick, disabled, className)}
            {addSeparator && <div className={styles.verticalSeparator} />}
        </>
    );
}

function getTableView() {
    const tableViewId = listViewStore?.selectedTableViewId;
    return listViewStore?.tableViews?.get(tableViewId);
}

function getInstrumentationContexts(): InstrumentationContext[] {
    const tableView = getTableView();
    if (tableView?.tableQuery?.type == TableQueryType.Search) {
        const selectedRows = [...tableView.selectedRowKeys.keys()];
        return getInstrumentationContextsFromTableView(selectedRows, tableView);
    }
    return null;
}

/**
 * Helper function to stop event propagation and log datapoints
 */
function handleActionClicked(
    datapoint: string,
    isSingleRecipient: boolean,
    event?: React.MouseEvent<unknown>
) {
    event?.stopPropagation();
    logUsage(datapoint);

    // When user's setting is newestOnBottom, control flight displays GlobalReplyButton
    // whereas treatment renders GlobalReplyBar. Getting the number of times an action other
    // than the one on GlobalReplyButton indicates the usefulness of having all three actions on the bar.
    if (isNewestOnBottom()) {
        if (isSingleRecipient) {
            // Global Reply Button has label Reply. Log F as others.
            datapoint !== Datapoint.ReplyAction && logUsage(Datapoint.OtherGlobalAction);
        } else {
            // Log R and F as others.
            datapoint !== Datapoint.ReplyAllAction && logUsage(Datapoint.OtherGlobalAction);
        }
    }
}
